import type { AxiosResponse } from 'axios';

import type { ApiBody, ApiInput, ApiOutput, ApiResponseOutput, DataEndpoint, ResponseEndpoint } from '../utils';
import { apiVersion, getNoddiUrl, type ResponseType } from './url';

/**
 * Single-source endpoint definitions.
 *
 * Declare each endpoint once with {@link dataEndpoint} / {@link responseEndpoint}.
 * Resource maps register the public URL keys; a definition may remain outside
 * its domain map when another registry owns its only public key. `resources.ts`
 * merges those maps and derives the registry keys and request/response types
 * centrally — no per-file `URLKeys` / `ServerRequests` boilerplate.
 *
 * The choice between the two helpers is about the **return shape only**, not the
 * HTTP verb: `dataEndpoint` resolves to the parsed body, `responseEndpoint` to the
 * full `AxiosResponse`. The verb (and caching) lives entirely in the hook layer
 * (`useGet` / `usePost` / `usePatch` / `useDelete` / `usePostAsGetQuery`) — e.g. a
 * `useDelete` call site may consume either declaration.
 *
 * ```ts
 * export const getCarUrls = {
 *   getCar: dataEndpoint<CarGetApiData, CarGetApiResponses>({
 *     getUrl: ({ carId }) => apiUrl(`cars/${carId}/`)
 *   })
 * };
 * ```
 *
 * The runtime shape of each entry matches the legacy `get<Resource>Urls` value
 * (`getUrl`, `getBody`, `responseType`, …), so the `noddiAsync` hook pipeline
 * (auth, camelCase, query keys, invalidation) is untouched — this is a pure
 * authoring-ergonomics change.
 */

type InputOrVoid<Input> = [Input] extends [void] ? void : Input;

type GetBodyFn<Input> = (input: InputOrVoid<Input>) => Partial<Input> | FormData;

/**
 * `getBody` is **required** whenever the endpoint's `*ApiData` declares a body —
 * required or optional (derived from {@link ApiBody}) — so a body-bearing
 * POST/PATCH can't be declared without forwarding its body. Optional bodies are
 * included on purpose: the generator marks an all-optional-field PATCH body
 * `body?: X`, and omitting `getBody` there silently fires an empty payload.
 */
type GetBodyClause<Data, Input> = [ApiBody<Data>] extends [never]
  ? { getBody?: GetBodyFn<Input> }
  : { getBody: GetBodyFn<Input> };

/** The config a caller writes per endpoint — everything except the inferred types. */
type EndpointConfig<Data, Input, Output> = {
  getUrl: (input: InputOrVoid<Input>) => string;
  responseType?: ResponseType;
  handleRes?: (res: AxiosResponse<Output>) => Promise<unknown>;
  getHeaders?: (input: InputOrVoid<Input>) => Record<string, string>;
  ignoreImpersonation?: boolean;
  skipConvertingToCamelCase?: boolean;
  skipBodyTransformation?: boolean;
  /** Side-effect GET: useGet merges gcTime: 0 by default; caller queryConfig overrides. */
  sideEffectGet?: true;
} & GetBodyClause<Data, Input>;

/**
 * A stored endpoint definition. `__server` is a phantom (type-only) field that
 * carries the resolved {@link DataEndpoint} / {@link ResponseEndpoint} so
 * {@link InferServerRequests} can reconstruct the `ServerRequests` map; it never
 * exists at runtime.
 */
interface EndpointDef<Server, Input> {
  getUrl: (input: InputOrVoid<Input>) => string;
  getBody?: GetBodyFn<Input>;
  responseType?: ResponseType;
  handleRes?: (res: AxiosResponse) => Promise<unknown>;
  getHeaders?: (input: InputOrVoid<Input>) => Record<string, string>;
  ignoreImpersonation?: boolean;
  skipConvertingToCamelCase?: boolean;
  skipBodyTransformation?: boolean;
  sideEffectGet?: true;
  readonly __server?: Server;
}

/**
 * Declare an endpoint whose hook resolves to the parsed response **body** (the
 * typical shape for `useGet` / `useDelete` consumers). The helper name reflects
 * the return shape, not the HTTP verb — the verb lives in the hook. `Input`
 * defaults to the flattened `ApiInput<Data>`; override it for endpoints whose
 * input deviates.
 */
export function dataEndpoint<Data, Responses extends Record<number, unknown>, Input = ApiInput<Data>>(
  config: EndpointConfig<Data, Input, ApiOutput<Responses>>
): EndpointDef<DataEndpoint<Data, Responses, Input>, Input> {
  return { ...config };
}

/**
 * Declare an endpoint whose hook resolves to the full `AxiosResponse` (the
 * typical shape for `usePost` / `usePatch` consumers). The helper name reflects
 * the return shape, not the HTTP verb. `Input` defaults to the flattened
 * `ApiInput<Data>`; override it for custom payloads (e.g. FormData).
 */
export function responseEndpoint<Data, Responses extends Record<number, unknown>, Input = ApiInput<Data>>(
  config: EndpointConfig<Data, Input, ApiResponseOutput<Responses>>
): EndpointDef<ResponseEndpoint<Data, Responses, Input>, Input> {
  return { ...config };
}

/** Build a `/v1/...`-prefixed URL. Sugar over `getNoddiUrl({ path: \`${apiVersion}...\` })`. */
export function apiUrl(path: string, queryParams?: unknown): string {
  return getNoddiUrl({ path: `${apiVersion}${path}`, queryParams });
}

/** `{ getCar: 'getCar', ... }` from a map — builds the registry's central `URLKeys`. */
export function keyMirror<T extends object>(obj: T): { readonly [K in keyof T]: K } {
  return Object.fromEntries(Object.keys(obj).map((key) => [key, key])) as { [K in keyof T]: K };
}

/** Reconstruct the `ServerRequests` type (input/output per key) from a merged `get*Urls` map. */
export type InferServerRequests<T extends Record<string, { readonly __server?: unknown }>> = {
  [K in keyof T]: NonNullable<T[K]['__server']>;
};
